import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';
import { EntityService } from 'shared/core/services/entity.service';
import { ServerService } from 'shared/core/services/server.service';

export interface RoomsMeLoadStatus {
    rooms: number;
    me: number;
}

/**
 * Owns the Rooms/M&E import load status shown in the header nav.
 *
 * it was inside DashboardHeaderNavComponent, which is declared in three templates (header-layout, full-layout, dashboard.page)
 * so every instantiated header ran its own polling timer against `dashboard/import-control/getRoomsMeStatus`
 *
 * changes:
 *  - One timer for the whole app (providedIn: 'root', ref-counted start/stop)
 *  - Poll interval raised from 5 to 20 minutes.
 *  - A TTL cache keyed by the sorted hotel selection. Re selecting the same hotels, or returning to a tab, does not refetch while the data is fresh
 *  - The visibility handler goes through the same TTL gate instead of the old 2-second throttle, which made alt-tabbing effectively unthrottled
 */
@Injectable({ providedIn: 'root' })
export class ImportLoadStatusService implements OnDestroy {
    private static readonly ENDPOINT = 'dashboard/import-control/getRoomsMeStatus';

    private static readonly MAX_GET_QUERY_LENGTH = 2000;

    // how long a result stays fresh before a refresh is allowed
    private readonly TTL_MS = 20 * 60 * 1000;

    // background poll cadence. Matches the TTL so the poll is the only driver
    private readonly POLL_INTERVAL_MS = 20 * 60 * 1000;

    private readonly status$ = new BehaviorSubject<RoomsMeLoadStatus>({
        rooms: 0,
        me: 0,
    });

    private cacheKey: string | null = null;
    private fetchedAt = 0;

    private inFlightKey: string | null = null;

    private requestSeq = 0;

    private pollTimer: ReturnType<typeof setInterval> | null = null;
    private consumers = 0;
    private selectionSubscriptions: Subscription[] = [];

    private readonly visibilityChangeHandler: () => void;

    constructor(
        private serverService: ServerService,
        private entityService: EntityService
    ) {
        this.visibilityChangeHandler = (): void => {
            if (document.visibilityState === 'visible') {
                this.refresh();
            }
        };
    }

    public get loadStatus$(): Observable<RoomsMeLoadStatus> {
        return this.status$.asObservable();
    }

    public start(): void {
        this.consumers += 1;
        if (this.consumers > 1) {
            return;
        }

        document.addEventListener('visibilitychange', this.visibilityChangeHandler);
        this.pollTimer = setInterval(() => {
            if (document.hidden) {
                return;
            }
            this.refresh();
        }, this.POLL_INTERVAL_MS);

        this.watchSelection();
        this.refresh();
    }

    private watchSelection(): void {
        this.selectionSubscriptions.push(
            this.entityService.on_change.subscribe(() => this.refresh()),
            this.entityService.on_selectAll.subscribe(() => this.refresh())
        );
    }

    public stop(): void {
        this.consumers = Math.max(0, this.consumers - 1);
        if (this.consumers > 0) {
            return;
        }
        this.teardown();
    }

    public refresh(force = false): void {
        if (!this.serverService.shouldMakeApiCalls()) {
            return;
        }

        const htlIds = this.entityService?.selectedHtlId;
        if (!htlIds || htlIds.length === 0) {
            return;
        }

        // Nothing is selected at the entity level yet
        if (
            !this.entityService.selectedEntityIds ||
            this.entityService.selectedEntityIds.length === 0
        ) {
            return;
        }

        const key = this.buildKey(htlIds);

        if (this.inFlightKey === key) {
            return;
        }

        const isFresh =
            key === this.cacheKey && Date.now() - this.fetchedAt < this.TTL_MS;
        if (isFresh && !force) {
            return;
        }

        this.fetch(key, htlIds);
    }

    // drop the cache so the next refresh always hits the network (after logout for example)
    public invalidate(): void {
        this.cacheKey = null;
        this.fetchedAt = 0;
        this.inFlightKey = null;
    }

    private fetch(key: string, htlIds: number[]): void {
        const seq = ++this.requestSeq;
        this.inFlightKey = key;

        const csv = htlIds.join(',');

        const isStale = (): boolean => seq !== this.requestSeq;

        const onSuccess = (data: { metric: string; load_percentage: number }[]): void => {
            if (isStale()) {
                return;
            }
            this.inFlightKey = null;

            this.cacheKey = key;
            this.fetchedAt = Date.now();

            const next: RoomsMeLoadStatus = { rooms: 0, me: 0 };
            (data || []).forEach(item => {
                if (item.metric === 'Rooms') {
                    next.rooms = item.load_percentage;
                } else if (item.metric === 'M&E') {
                    next.me = item.load_percentage;
                }
            });
            this.status$.next(next);
        };

        const onError = (err: unknown): void => {
            if (isStale()) {
                return;
            }
            this.inFlightKey = null;
            // Don't cache a failure - leave cacheKey/fetchedAt alone so the next refresh retries instead of trusting data we never received
            console.error('[ImportLoadStatusService] getRoomsMeStatus failed', err);
        };

        if (this.fitsInQueryString(csv)) {
            // Small selection: GET, so the browser can serve a repeat from cache.
            this.serverService.get(
                ImportLoadStatusService.ENDPOINT,
                { htl_id: csv },
                onSuccess,
                onError,
                undefined,
                true // avoidLoading no spinner
            );
            return;
        }

        // Large selection: the id list does not belong in a URL (see MAX_GET_QUERY_LENGTH)
        this.serverService.post(
            ImportLoadStatusService.ENDPOINT,
            { htl_id: htlIds },
            onSuccess,
            onError,
            undefined,
            true // avoidLoading
        );
    }

    // Whether the id list is small enough to travel in the query string
    private fitsInQueryString(csv: string): boolean {
        return (
            encodeURIComponent(csv).length <= ImportLoadStatusService.MAX_GET_QUERY_LENGTH
        );
    }

    private buildKey(htlIds: number[]): string {
        return [...htlIds].sort((a, b) => a - b).join(',');
    }

    private teardown(): void {
        if (this.pollTimer) {
            clearInterval(this.pollTimer);
            this.pollTimer = null;
        }
        document.removeEventListener('visibilitychange', this.visibilityChangeHandler);

        for (const sub of this.selectionSubscriptions) {
            sub.unsubscribe();
        }
        this.selectionSubscriptions = [];

        this.requestSeq += 1;
        this.inFlightKey = null;
    }

    public ngOnDestroy(): void {
        this.consumers = 0;
        this.teardown();
    }
}
